import React, { useEffect, useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Chip,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { learnerService, LearningEvidenceItem } from '../../services/learnerService';

const SOURCE_LABEL: Record<string, string> = {
  assessment: 'Placement check',
  practice: 'Practice',
  recheck: 'Recheck',
  observation: 'Observation',
  parent_override: 'Your correction',
};

interface EvidenceDrawerProps {
  open: boolean;
  onClose: () => void;
  learnerId: string;
  skillId: string;
  skillName: string;
}

/**
 * Per-skill evidence drill-down (Phase 6). Leads with evidence, not
 * adjectives: raw, dated, item-level data behind every mastery claim,
 * calling the evidence route Phase 2 already built
 * (GET /api/learners/:learnerId/evidence?skillId=) rather than a new one.
 */
const EvidenceDrawer: React.FC<EvidenceDrawerProps> = ({ open, onClose, learnerId, skillId, skillName }) => {
  const [evidence, setEvidence] = useState<LearningEvidenceItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const token = localStorage.getItem('token') || '';

    setLoading(true);
    setError(null);
    learnerService
      .getEvidence(learnerId, token, { skillId, limit: 100 })
      .then((data) => {
        if (cancelled) return;
        setEvidence(data.evidence || []);
      })
      .catch((err) => {
        console.error('Error fetching evidence:', err);
        if (!cancelled) setError('Unable to load the evidence right now.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, learnerId, skillId]);

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: { xs: '100vw', sm: 420 }, p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6">{skillName}</Typography>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Every recorded attempt behind this skill's status, most recent first.
        </Typography>
        <Divider sx={{ mb: 1 }} />

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        )}
        {error && <Alert severity="warning" sx={{ mt: 2 }}>{error}</Alert>}
        {!loading && !error && evidence.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            No attempts recorded yet.
          </Typography>
        )}

        <List dense>
          {evidence.map((item) => (
            <ListItem key={item._id} divider disableGutters>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {item.correct === true && <Chip label="Correct" color="success" size="small" />}
                    {item.correct === false && <Chip label="Incorrect" color="error" size="small" variant="outlined" />}
                    {item.correct === null && <Chip label="Recorded" color="default" size="small" variant="outlined" />}
                    <Typography variant="body2" color="text.secondary">
                      {SOURCE_LABEL[item.source] || item.source}
                    </Typography>
                  </Box>
                }
                secondary={new Date(item.observedAt).toLocaleDateString(undefined, {
                  year: 'numeric', month: 'short', day: 'numeric',
                })}
              />
            </ListItem>
          ))}
        </List>
      </Box>
    </Drawer>
  );
};

export default EvidenceDrawer;
